import objectPath from 'object-path'
import dayjs from 'dayjs'
import { getCurrentUi } from 'designer/component/action/Data'
import {
  DEFAULT_DATE,
  DEFAULT_DATETIME,
  DEFAULT_TIME,
  getDateTimeFormValue,
} from 'designer/component/action/Date'

const getRuleSetTarget = (action = {}, target = '') => {
  return (
    target ||
    action.target ||
    action.targetKey ||
    action.key ||
    action.valueKey ||
    action.targetName ||
    action.targetOptionValue ||
    action.formDataKey ||
    ''
  )
}

const addRuleSetKey = (keys = [], key = '') => {
  if (!key) return keys
  if (keys.includes(key)) return keys

  keys.push(key)
  return keys
}

const getRuleSetShortKey = (key = '') => {
  let keyList = String(key || '')
    .split('.')
    .filter((item) => item)

  return keyList[keyList.length - 1] || ''
}

const getRuleSetFieldKey = (field = {}, fallbackKey = '') => {
  return (
    fallbackKey ||
    field.target ||
    field.optionValue ||
    field.key ||
    field.targetKey ||
    field.fieldKey ||
    field.field ||
    field.valueKey ||
    ''
  )
}

const getRuleSetFieldName = (field = {}) => {
  return (
    field.valueKey ||
    field.name ||
    field.fieldPath ||
    field.targetName ||
    field.fieldName ||
    ''
  )
}

const getRuleSetOptionParts = (field = {}) => {
  return [
    field.fullKey,
    field.optionKey,
    field.actionOptionValue,
    field.targetOptionValue,
    field.fieldPath,
    field.name,
  ]
    .filter((value) => value)
    .flatMap((value) =>
      String(value)
        .split(/[|:]/)
        .map((item) => item.trim())
        .filter((item) => item),
    )
}

const getRuleSetMatchParts = (value = '') =>
  String(value || '')
    .split(/[|:]/)
    .map((item) => item.trim())
    .filter((item) => item)

const getRuleSetFieldMatchValues = (field = {}, fallbackKey = '') => {
  return [
    fallbackKey,
    field.target,
    field.optionValue,
    field.fieldKey,
    field.targetKey,
    field.key,
    field.targetOptionValue,
    field.actionOptionValue,
    field.optionKey,
    field.fullKey,
  ].flatMap((value) => getRuleSetMatchParts(value))
}

const getRuleSetTabOptionMatchValues = (option = {}) => {
  let optionValue =
    option.optionValue ||
    (option.formComponentKey && option.key
      ? `${option.formComponentKey}.${option.key}`
      : '')

  return [
    optionValue,
    option.fullKey,
    option.optionKey,
    option.key,
  ].flatMap((value) => getRuleSetMatchParts(value))
}

const getRuleSetTabFormOption = (
  designContext = {},
  field = {},
  fallbackKey = '',
) => {
  let tabFormOption =
    objectPath.get(designContext, 'isUiChanged.tabFormOption') || []
  let fieldMatchValues = getRuleSetFieldMatchValues(field, fallbackKey)
  let targetMatchValues = [
    fallbackKey,
    field.target,
    field.optionValue,
  ].flatMap((value) => getRuleSetMatchParts(value))

  return tabFormOption.reduce(
    (result, option) => {
      let optionValue =
        option.optionValue ||
        (option.formComponentKey && option.key
          ? `${option.formComponentKey}.${option.key}`
          : '')
      let score = getRuleSetTabOptionMatchValues(option).reduce(
        (total, value) => {
          if (!fieldMatchValues.includes(value)) return total

          return total + (value.includes('.') ? 10 : 1)
        },
        0,
      )

      if (optionValue && targetMatchValues.includes(optionValue)) {
        score += 100
      }
      if (
        option.formComponentKey &&
        field.target &&
        String(field.target).indexOf(`${option.formComponentKey}.`) === 0 &&
        fieldMatchValues.includes(option.key)
      ) {
        score += 50
      }

      if (score <= result.score) return result

      return {
        option,
        score,
      }
    },
    {
      option: null,
      score: 0,
    },
  ).option
}

const getRuleSetFieldContext = (
  designContext = {},
  field = {},
  fallbackKey = '',
) => {
  let tabFormOption = getRuleSetTabFormOption(
    designContext,
    field,
    fallbackKey,
  )
  let formComponentName =
    field.formComponentName || tabFormOption?.formComponentName || ''
  let jsonForm =
    (formComponentName &&
      objectPath.get(designContext, [
        'isUiChanged',
        'tabForm',
        formComponentName,
        'jsonForm',
      ])) ||
    designContext.jsonForm ||
    {}

  if (!tabFormOption) {
    return {
      field,
      formComponentName,
      jsonForm,
    }
  }

  return {
    field: {
      ...tabFormOption,
      ...field,
      fullKey: field.fullKey || tabFormOption.fullKey,
      key: field.key || tabFormOption.key,
      optionKey: field.optionKey || tabFormOption.optionKey,
      optionValue: field.optionValue || tabFormOption.optionValue,
      valueKey: field.valueKey || tabFormOption.value,
      name: field.name || tabFormOption.name,
      formComponentName,
    },
    formComponentName,
    jsonForm,
  }
}

const getRuleSetFullKey = (jsonForm = {}, field = {}, fallbackKey = '') => {
  if (field.fullKey) return field.fullKey

  let mapKey = objectPath.get(jsonForm, 'schema.mapKey') || {}
  let mapName = objectPath.get(jsonForm, 'schema.mapName') || {}
  let optionParts = getRuleSetOptionParts(field)
  let mapKeyValues = Object.values(mapKey)
  let foundOptionFullKey = optionParts.find((part) =>
    mapKeyValues.includes(part),
  )

  if (foundOptionFullKey) return foundOptionFullKey

  let fieldPath = field.fieldPath || field.name
  let foundMapNameKey = Object.keys(mapName).find(
    (key) => fieldPath && mapName[key] === fieldPath,
  )

  if (foundMapNameKey) return mapKey[foundMapNameKey]

  let fieldKey = getRuleSetShortKey(getRuleSetFieldKey(field, fallbackKey))
  if (fieldKey && mapKey[fieldKey]) return mapKey[fieldKey]

  return ''
}

const getRuleSetCurrentUi = (jsonForm = {}, key = '') => {
  let uiKey = getRuleSetShortKey(key)

  if (!uiKey || !jsonForm.schema || !jsonForm.uischema) return {}

  return getCurrentUi(uiKey, jsonForm.schema, jsonForm.uischema) || {}
}

const isRuleSetScopeUi = (ui = {}) =>
  ['collapse', 'panel'].includes(ui.tagname)

const getRuleSetScopedKeys = (
  designContext = {},
  field = {},
  fallbackKey = '',
) => {
  let fieldContext = getRuleSetFieldContext(
    designContext,
    field,
    fallbackKey,
  )
  let { jsonForm = {}, formComponentName = '' } = fieldContext
  field = fieldContext.field
  let fullKey = getRuleSetFullKey(jsonForm, field, fallbackKey)
  let keyList = String(fullKey || '')
    .split('.')
    .filter((key) => key)
  let keys = []

  if (keyList.length === 0) return keys

  let fieldKey = getRuleSetShortKey(getRuleSetFieldKey(field, fallbackKey))
  let fieldName = getRuleSetShortKey(getRuleSetFieldName(field))
  let fieldUi = getRuleSetCurrentUi(jsonForm, fieldKey)
  let fieldNames = []

  addRuleSetKey(fieldNames, fieldKey)
  if (fieldUi?.key === fieldKey) {
    addRuleSetKey(fieldNames, fieldUi.name)
  }
  addRuleSetKey(fieldNames, fieldName)

  keyList
    .slice(0, -1)
    .reverse()
    .forEach((scopeKey) => {
      let scopeUi = getRuleSetCurrentUi(jsonForm, scopeKey)
      if (!scopeUi?.name || !isRuleSetScopeUi(scopeUi)) return

      fieldNames.forEach((name) => {
        addRuleSetKey(keys, `@${scopeUi.name}.${name}`)
      })
    })

  if (formComponentName) {
    let formComponentKeys = []

    keys.forEach((key) => {
      addRuleSetKey(formComponentKeys, `${formComponentName}.${key}`)
    })
    fieldNames.forEach((name) => {
      addRuleSetKey(
        formComponentKeys,
        `${formComponentName}.@${formComponentName}.${name}`,
      )
      addRuleSetKey(formComponentKeys, `${formComponentName}.${name}`)
    })

    return formComponentKeys
  }

  return keys
}

const getRuleSetUi = (designContext = {}, targetKey = '') => {
  let { jsonForm = {} } = designContext
  let ui = designContext.getCurrentUi
    ? designContext.getCurrentUi(targetKey)
    : getCurrentUi(targetKey, jsonForm.schema, jsonForm.uischema)

  return ui || {}
}

const getRuleSetFieldValue = (
  designContext = {},
  field = {},
  data = {},
) => {
  if (field.source && field.source !== 'formData') return undefined

  let formData = data.formData || designContext.jsonForm?.formData || {}
  let keys = getRuleSetScopedKeys(designContext, field)

  addRuleSetKey(keys, getRuleSetFieldKey(field))
  addRuleSetKey(keys, getRuleSetFieldName(field))

  let foundValue
  keys.find((key) => {
    if (!objectPath.has(formData, key)) return false

    foundValue = objectPath.get(formData, key)
    return true
  })

  return foundValue
}

const getRuleSetDefaultValue = (ui = {}) => {
  if (ui.tagname === 'date') {
    return {
      ...DEFAULT_DATE,
      custom: ui.defaultValue,
    }[ui.defaultType]
  }
  if (ui.tagname === 'time') {
    return {
      ...DEFAULT_TIME,
      custom: ui.defaultValue,
    }[ui.defaultType]
  }
  if (ui.tagname === 'dateTime') {
    return {
      ...DEFAULT_DATETIME,
      current: getDateTimeFormValue(new Date(), ui?.timestamp),
      custom: getDateTimeFormValue(ui.defaultValue, ui?.timestamp),
    }[ui.defaultType]
  }

  return ui.defaultValue
}

const getRuleSetLegacyValue = (
  designContext = {},
  action = {},
  data = {},
  targetKey = '',
) => {
  let { user = {}, urlParams = {}, isUiChanged = {} } = designContext
  let formData = data.formData || designContext.jsonForm?.formData || {}
  let ui = getRuleSetUi(designContext, targetKey)
  let { valueType, valueTo = {}, replaceTo = {}, customValue } = action
  let value = objectPath.get(action, 'isSet.value') || action.value || {}

  if (valueType === 'today') {
    if (ui.tagname === 'date') return dayjs().format('YYYY-MM-DD')
    if (ui.tagname === 'time') return dayjs().format('HH:mm:ss')
    if (ui.tagname === 'dateTime') {
      return getDateTimeFormValue(new Date(), ui?.timestamp)
    }
  } else if (valueType === 'defaultValue') {
    return getRuleSetDefaultValue(ui)
  } else if (valueType === 'perspective') {
    return objectPath.get(urlParams, 0)
  } else if (valueType === 'userId') {
    return user.userId
  } else if (valueType === 'employeeCode') {
    return user.employeeCode
  } else if (valueType === 'comingTable') {
    let isWaitUpsert = objectPath.get(isUiChanged, 'isWaitUpsert')
    if (isWaitUpsert?.key === targetKey) {
      return objectPath.get(isWaitUpsert, 'table')
    }
  } else if (valueType === 'rowTable') {
    return objectPath.get(data.rowData || {}, valueTo.field)
  } else if (valueType === 'replaceValue') {
    let valueReplace
    if (replaceTo.groupType === 'queryParam') {
      let datasourceValue = objectPath.get(designContext, '@datasource')
      valueReplace = objectPath.get(
        datasourceValue,
        `${replaceTo.groupType}.${replaceTo.key}`,
      )
    } else {
      valueReplace = objectPath.get(formData, replaceTo.key)
    }

    let oldString = objectPath.get(value, targetKey) || ''
    if (oldString && valueReplace) {
      return oldString.replace(`@${replaceTo.key}`, valueReplace)
    }
    return oldString.replace(`@${replaceTo.key}`, '')
  } else if (valueType === 'custom') {
    return customValue
  }

  return objectPath.get(value, targetKey)
}

const getRuleSetValue = (
  designContext = {},
  action = {},
  data = {},
  options = {},
  targetKey = '',
) => {
  let { getRuleActionValue = () => action.value } = options
  let valueType = action.valueType || action.mode
  if (valueType === 'field' && action.valueFrom) {
    let fieldValue = getRuleSetFieldValue(
      designContext,
      action.valueFrom,
      data,
    )

    if (fieldValue !== undefined) return fieldValue
  }

  let legacyValueType = [
    'today',
    'defaultValue',
    'perspective',
    'userId',
    'employeeCode',
    'comingTable',
    'rowTable',
  ].includes(valueType)

  if (legacyValueType) {
    return getRuleSetLegacyValue(
      designContext,
      {
        ...action,
        valueType,
      },
      data,
      targetKey,
    )
  }

  return getRuleActionValue(designContext, action, data)
}

const setRuleSetFormData = (
  designContext = {},
  targetKeys = [],
  value,
) => {
  let {
    jsonForm = {},
    setFieldFormData,
    setFieldAllData,
    setFormData,
    isUiChanged = {},
    setIsUiChanged,
  } = designContext
  let formData = jsonForm.formData || {}

  targetKeys.forEach((key) => {
    objectPath.set(formData, key, value)
    objectPath.set(isUiChanged, key, { isRender: true })
    if (setFieldFormData) setFieldFormData(key, value)
    if (setFieldAllData) setFieldAllData(key, value)
  })
  if (setFormData) setFormData(formData)
  if (setIsUiChanged) setIsUiChanged(isUiChanged)
}

const setRuleSetDynamicFormData = (
  designContext = {},
  action = {},
  data = {},
  value,
) => {
  let { setFieldFormData } = designContext
  let { dynamicName, dynamicKey, dynamicIndex } = data
  let valueKey = action.valueKey || action.targetName || action.key

  if (!setFieldFormData || !valueKey) return false

  setFieldFormData(`${dynamicKey}.${dynamicIndex}.${valueKey}`, value)
  setFieldFormData(`${dynamicName}.${dynamicIndex}.${valueKey}`, value)

  return true
}

const setRuleSetSearchData = (
  designContext = {},
  ui = {},
  targetKey = '',
  value,
  data = {},
) => {
  let { setFieldSearchData } = designContext
  if (data.isPage !== 1 || !setFieldSearchData) return

  setFieldSearchData(ui, targetKey, value)
  if (ui.name) setFieldSearchData(ui, ui.name, value)
}

export const onRuleSet = (
  designContext = {},
  action = {},
  data = {},
  options = {},
) => {
  let {
    setRuleFormData = () => undefined,
    target = '',
  } = options
  let targetKey = getRuleSetTarget(action, target)
  if (!targetKey) return undefined

  let ui = getRuleSetUi(designContext, targetKey)
  let value = getRuleSetValue(
    designContext,
    action,
    data,
    options,
    targetKey,
  )

  if (data.isDynamic && setRuleSetDynamicFormData(
    designContext,
    action,
    data,
    value,
  )) {
    return value
  }

  setRuleSetSearchData(designContext, ui, targetKey, value, data)
  let targetKeys = getRuleSetScopedKeys(designContext, action, targetKey)
  if (targetKeys.length > 0) {
    setRuleSetFormData(designContext, targetKeys, value)
    return value
  }

  setRuleFormData(designContext, targetKey, value)

  return value
}
